import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHomeUsePluginContainer } from "../../../custom-hooks/HomeUsePluginRoot";
import { fotActions } from "../../../store/fot";
import { GuideContainerProps } from "./GuideContainer";
import { GuideModalProps } from "./GuideModal";
import {
  cotVideo,
  endTitle,
  guideCotEndSubtitle,
  guideCotSubTitle,
  guideManualEndSubtitle,
  guideManualSubTitle,
  manualVideo,
  title,
} from "./constants";

export type TriggerMessage = number | null | (() => void);
type TriggerType = "cot" | "manual";

export const useGuideContainer = ({ addNewProject, showNavPage }: GuideContainerProps) => {
  const dispatch = useDispatch();
  const {
    guideTriggerMessageState: { guideTriggerMessage, imagicaStudioTutorial },
  } = useHomeUsePluginContainer();

  const guideModalRef = useRef<{ triggerType: TriggerType; triggerCallback: () => void } | null>(null);

  const [guideConfig, setGuideConfig] = useState<GuideModalProps | null>();

  const handleTrigger = (triggerType: TriggerType, triggerMessage: TriggerMessage) => {
    const triggerCallback = triggerMessage instanceof Function ? triggerMessage : () => {};

    if (guideModalRef.current !== null) {
      return triggerCallback();
    }
    if (triggerType === "cot" && imagicaStudioTutorial.showCotGuide === true) {
      return triggerCallback();
    }
    if (triggerType === "manual" && imagicaStudioTutorial.showManualGuide === true) {
      return triggerCallback();
    }
    executeTrigger(triggerType, triggerCallback);
  };

  const executeTrigger = (triggerType: TriggerType, triggerCallback: () => void) => {
    guideModalRef.current = { triggerType, triggerCallback };
    handleSetModal();
  };

  const handleEnded = () => {
    if (showNavPage) {
      addNewProject();
    }

    closeGuide();
  };

  const closeGuide = () => {
    setGuideConfig(null);
    guideModalRef.current?.triggerCallback();
    guideModalRef.current = null;
  };

  const handleSetModal = () => {
    switch (guideModalRef.current?.triggerType) {
      case "cot":
        setGuideConfig({
          copy: {
            start: { title, subTitle: guideCotSubTitle },
            end: { title: endTitle, subTitle: guideCotEndSubtitle },
          },
          src: cotVideo,
          onClose: handleEnded,
          onFinish: handleEnded,
        });
        dispatch(fotActions.setImagicaStudioTutorial({ ...imagicaStudioTutorial, showCotGuide: true }));
        break;
      case "manual":
        setGuideConfig({
          copy: {
            start: { title, subTitle: guideManualSubTitle },
            end: { title: endTitle, subTitle: guideManualEndSubtitle },
          },
          src: manualVideo,
          onClose: closeGuide,
          onFinish: closeGuide,
        });
        dispatch(fotActions.setImagicaStudioTutorial({ ...imagicaStudioTutorial, showManualGuide: true }));
        break;
      default:
        setGuideConfig(null);
        break;
    }
  };

  useEffect(() => {
    if (guideTriggerMessage.cot) {
      handleTrigger("cot", guideTriggerMessage.cot);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideTriggerMessage.cot]);

  useEffect(() => {
    if (guideTriggerMessage.manual) {
      handleTrigger("manual", guideTriggerMessage.manual);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideTriggerMessage.manual]);

  return {
    guideConfig,
  };
};
